import { useAuth0 } from '@auth0/auth0-vue'
import { ThymeEventError } from '@thyme/libs/src/errors/errors'
import { getLogger } from '@thyme/libs/src/logging/logger'
import { unref } from 'vue'
import { RouteLocationNormalized } from 'vue-router'
import { sendLoggedInMonitoringEvent } from '@/libs/events/events'
import {
  clearAllLoginTracking,
  getAllMethodsTried,
  getElapsedMs,
  getLoginTrackingState,
  getMostRecentAttempt,
} from '@/libs/events/loginTracking'
import { MonitoringEventName } from '@/libs/events/types'
import { useLoggedInEntityId } from '@/libs/state/auth0Client'

export const AUTH_CALLBACK_NAME = '#authCallback'

/**
 *
 * @param to
 */
function sendTrackingEvent(to: RouteLocationNormalized) {
  try {
    const entityId = useLoggedInEntityId()
    sendLoggedInMonitoringEvent(
      { event: MonitoringEventName.LOGIN, data: { target: to.fullPath } },
      entityId
    )
  } catch (err: any) {
    const error = ThymeEventError.fromError(err)
    getLogger().error('Error sending monitoring event', error)
  }
}

/**
 * Fire loginSucceeded event after successful auth
 * Includes all methods tried as a summary of the login journey
 * @param to The destination route
 */
function sendLoginSuccessEvents(to: RouteLocationNormalized): void {
  const state = getLoginTrackingState()
  if (!state || state.attempts.length === 0) {
    return
  }

  try {
    const entityId = useLoggedInEntityId()
    const elapsedMs = getElapsedMs()
    const mostRecentAttempt = getMostRecentAttempt()
    const methods = getAllMethodsTried()

    // Fire loginSucceeded with all methods tried as a summary
    if (mostRecentAttempt) {
      sendLoggedInMonitoringEvent(
        {
          event: MonitoringEventName.LOGIN_SUCCEEDED,
          data: {
            method: mostRecentAttempt.method,
            targetUrl: to.fullPath,
            attemptId: mostRecentAttempt.attemptId,
            durationMs: elapsedMs,
            attemptedMethods: methods,
          },
        },
        entityId
      )
    }

    // Clear all tracking data on success
    clearAllLoginTracking()
  } catch (err: any) {
    const error = ThymeEventError.fromError(err)
    getLogger().error('Error sending login success events', error)
  }
}

/**
 * Add an auth guard unless the route is marked as publicly accessible
 * @param to
 * @param from
 */
export async function maybeAuthGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) {
  if (to.meta.unauthenticated) {
    return true
  }

  const client = useAuth0()
  if (!unref(client.isAuthenticated)) {
    await client.checkSession()
  }
  if (from.name === AUTH_CALLBACK_NAME) {
    sendTrackingEvent(to)
    sendLoginSuccessEvents(to)
  }

  if (unref(client.isAuthenticated)) {
    return true
  }
  return {
    name: '#loginMethod',
    query: { redirect: to.fullPath },
  }
}
